import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { AlertTriangle, RefreshCw, Wifi } from "lucide-react";

/**
 * Pings /api/health every 60s. If we get a server/network error twice in a row
 * we show a top banner: "Backend is sleeping — features may be missing".
 * Auto-hides when the ping succeeds.
 */
export default function BackendStatusBanner() {
  const [status, setStatus] = useState("ok"); // "ok" | "warming" | "down"
  const [retrying, setRetrying] = useState(false);
  const failsRef = useRef(0);

  const ping = async () => {
    try {
      const r = await api.get("/health", { timeout: 8000 });
      if (r.status === 200) {
        failsRef.current = 0;
        setStatus("ok");
      }
    } catch (e) {
      failsRef.current += 1;
      if (failsRef.current >= 2) setStatus("down");
      else setStatus("warming");
    }
  };

  useEffect(() => {
    ping();
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, []);

  const handleRetry = async () => {
    setRetrying(true);
    failsRef.current = 0;
    try {
      await ping();
    } finally {
      setRetrying(false);
    }
  };

  // Hard reset: unregister service workers, purge all caches, hard-reload.
  // Fixes the recurring "cached-old-bundle-after-deploy" problem in one click.
  const handleHardReset = async () => {
    setRetrying(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* silent */ }
    // Force network reload — bypasses http cache
    window.location.reload(true);
  };

  if (status === "ok") return null;

  const isDown = status === "down";
  return (
    <div
      data-testid="backend-status-banner"
      className={`sticky top-0 z-50 w-full px-3 py-2 flex items-center justify-between gap-2 text-xs border-b ${
        isDown
          ? "bg-rose-900/40 border-rose-500/40 text-rose-100"
          : "bg-amber-900/30 border-amber-500/30 text-amber-100"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isDown ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <Wifi className="w-4 h-4 shrink-0 animate-pulse" />}
        <span className="truncate">
          {isDown
            ? "Backend is sleeping or down. Data may not load — try Reload App, or redeploy if it persists."
            : "Server warming up… one moment."}
        </span>
      </div>
      <button
        onClick={handleRetry}
        disabled={retrying}
        className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded bg-black/30 hover:bg-black/50 text-white text-[11px]"
        data-testid="backend-status-retry"
      >
        <RefreshCw className={`w-3 h-3 ${retrying ? "animate-spin" : ""}`} />
        Reload
      </button>
      {isDown && (
        <button
          onClick={handleHardReset}
          disabled={retrying}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500/70 hover:bg-rose-500 text-white text-[11px] font-black"
          data-testid="backend-status-hard-reset"
          title="Clears cached assets and reloads from network — fixes stale bundle after redeploy"
        >
          Hard Reset
        </button>
      )}
    </div>
  );
}
